import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Company, UserCompany } from '@app/models/user.model';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { UserService } from '@app/services/user/user.service';
import { AllocationMode, AllocationType } from '@app/shared/enums';
import { CustomValidators } from '@app/shared/custom-validators';
import { catchError, debounceTime, distinctUntilChanged, map, of, Subject, switchMap, tap } from 'rxjs';
import { LocalizationService } from '../internationalization/localization.service';
import { mapUnallocatedJob, UnallocatedJobRow } from '../../models/unallocated-jobs.model';

@Component({
  selector: 'app-unallocated-jobs',
  templateUrl: './unallocated-jobs.component.html',
  styleUrl: './unallocated-jobs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UnallocatedJobsComponent implements OnInit, OnDestroy {
  readonly AllocationMode = AllocationMode;
  readonly jobs = signal<UnallocatedJobRow[]>([]);
  readonly isLoading = signal(true);
  readonly hasLoadError = signal(false);
  siteName = '';
  brandName = '';
  selectedJob?: UnallocatedJobRow;
  allocationMode = AllocationMode.INTERNAL;
  allocation = {
    email: '',
    companyName: '',
    sendWorkOrder: false
  };
  subconCompanies: Company[] = [];
  isLoadingCompanies = false;
  hasSearchedCompanies = false;
  isAllocating = false;
  allocationErrorKey = '';

  private readonly destroyRef = inject(DestroyRef);
  private readonly emailChanges = new Subject<string>();
  private readonly emailValidator = CustomValidators.isEmailValid();
  private readonly jobService = inject(JobService);
  private readonly userService = inject(UserService);
  private readonly navigationService = inject(NavigationService);
  private readonly localizationService = inject(LocalizationService);

  constructor() {
    this.navigationService.onChange(false, false);
    this.watchExternalEmailChanges();
  }

  get brands(): string[] {
    return [...new Set(this.jobs().map(job => job.brandName).filter((brand): brand is string => !!brand))].sort();
  }

  get filteredJobs(): UnallocatedJobRow[] {
    const siteName = this.siteName.trim().toLowerCase();
    return this.jobs().filter(job =>
      (!siteName || (job.siteName ?? '').toLowerCase().includes(siteName)) &&
      (!this.brandName || job.brandName === this.brandName));
  }

  get isExternalAllocation(): boolean {
    return this.allocationMode === AllocationMode.EXTERNAL;
  }

  get canConfirmAllocation(): boolean {
    if (!this.isEmailValid(this.allocation.email)) {
      return false;
    }

    return !this.isExternalAllocation || this.allocation.companyName.trim().length >= 5;
  }

  get filteredSubconCompanies(): Company[] {
    const typedName = this.allocation.companyName.trim().toLowerCase();
    return typedName
      ? this.subconCompanies.filter(company => company.name?.toLowerCase().includes(typedName))
      : this.subconCompanies;
  }

  get hasNoSubconCompanies(): boolean {
    return this.isExternalAllocation && this.hasSearchedCompanies &&
      !this.isLoadingCompanies && this.subconCompanies.length === 0;
  }

  async ngOnInit(): Promise<void> {
    try {
      const jobs = await firstValueFrom(this.jobService.getJobsUnallocated());
      this.jobs.set((jobs ?? []).map(mapUnallocatedJob));
    } catch {
      this.hasLoadError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  openAllocateTo(job: UnallocatedJobRow): void {
    if (!job.companyId) {
      return;
    }
    this.selectedJob = job;
    this.allocationMode = AllocationMode.INTERNAL;
    this.resetAllocationFields();
  }

  closeAllocateTo(): void {
    if (!this.isAllocating) {
      this.selectedJob = undefined;
      this.allocationErrorKey = '';
    }
  }

  changeAllocationMode(): void {
    this.resetAllocationFields();
  }

  changeAllocationEmail(): void {
    if (this.isExternalAllocation) {
      this.emailChanges.next(this.allocation.email);
    }
  }

  confirmAllocation(): void {
    if (!this.selectedJob || !this.canConfirmAllocation || this.isAllocating) {
      return;
    }

    const selectedJob = this.selectedJob;
    const emailAddress = this.allocation.email.trim();
    if (!selectedJob.companyId) {
      return;
    }

    const userCompany: UserCompany = {
      adminCompanyId: selectedJob.companyId,
      emailAddress,
      allocationType: this.isExternalAllocation ? AllocationType.EXTERNAL : AllocationType.INTERNAL,
      externalCompanyName: this.isExternalAllocation ? this.allocation.companyName.trim() : undefined
    };

    this.isAllocating = true;
    this.allocationErrorKey = '';
    this.userService.createUserCompany(userCompany).pipe(
      switchMap(createdUserCompany => {
        if (!createdUserCompany?.userId || !createdUserCompany?.allocateCompanyId) {
          return of(false);
        }

        return this.jobService.jobAllocation({
          jobId: selectedJob.id,
          allocatedUserEmail: createdUserCompany.emailAddress ?? emailAddress,
          allocationType: this.isExternalAllocation ? AllocationType.EXTERNAL : AllocationType.INTERNAL,
          companyId: createdUserCompany.allocateCompanyId
        });
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: allocated => {
        this.isAllocating = false;
        if (!allocated) {
          this.allocationErrorKey = 'open_jobs_list_allocate_error';
          return;
        }

        this.jobs.update(jobs => jobs.filter(job => job.id !== selectedJob.id));
        this.selectedJob = undefined;
        alert(this.localizationService.translate('open_jobs_list_allocate_success'));
      },
      error: () => {
        this.isAllocating = false;
        this.allocationErrorKey = 'open_jobs_list_allocate_error';
      }
    });
  }

  downloadWorkOrder(_job: UnallocatedJobRow): void {
    // Work-order downloading is not configured for this preview page yet.
  }

  ngOnDestroy(): void {
    this.navigationService.onChange(true, false);
  }

  private watchExternalEmailChanges(): void {
    this.emailChanges.pipe(
      map(email => email.trim()),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.allocation.companyName = '';
        this.subconCompanies = [];
        this.hasSearchedCompanies = false;
      }),
      switchMap(email => {
        if (!this.isExternalAllocation || !this.isEmailValid(email)) {
          return of<Company[]>([]);
        }

        this.isLoadingCompanies = true;
        return this.userService.getUserSubconCompaniesByEmail(email).pipe(
          map(companies => companies ?? []),
          tap(() => this.hasSearchedCompanies = true),
          catchError(() => {
            this.hasSearchedCompanies = true;
            return of<Company[]>([]);
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(companies => {
      this.isLoadingCompanies = false;
      this.subconCompanies = companies;
    });
  }

  private isEmailValid(email: string): boolean {
    return this.emailValidator(new FormControl(email.trim())) === null;
  }

  private resetAllocationFields(): void {
    this.allocation = { email: '', companyName: '', sendWorkOrder: false };
    this.subconCompanies = [];
    this.isLoadingCompanies = false;
    this.hasSearchedCompanies = false;
    this.allocationErrorKey = '';
  }
}
